// src/pages/Account.js
// Customer account dashboard — shows order history, personal details,
// delivery address, and security settings.
// Redirects to /login if no customer is authenticated.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CURRENCIES } from '../context/CurrencyContext';
import { Package, LogOut, User, MapPin, Lock, ArrowLeft, Truck, CheckCircle, Clock } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

// Map a Shopify currencyCode (e.g. "GBP") to its display symbol
const getSymbol = (currencyCode) => CURRENCIES.find(c => c.code === currencyCode)?.symbol || currencyCode || '';

const Account = () => {
    // Pull all auth actions and customer data from context
    const { customer, logout, loading, updateDetails, updateAddress, sendPasswordReset, authLoading } = useAuth();
    const navigate = useNavigate();

    // Active tab controls which section is shown
    const [activeTab, setActiveTab] = useState('orders');

    // selectedOrder shows the order detail view when set
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Feedback messages shown after form submissions
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // Personal details form — pre-filled from customer data
    const [detailsForm, setDetailsForm] = useState({
        firstName: customer?.firstName || '',
        lastName: customer?.lastName || '',
        phone: customer?.phone || '',
    });

    // Address form — pre-filled from customer's default address
    const [addressForm, setAddressForm] = useState({
        address1: customer?.defaultAddress?.address1 || '',
        address2: customer?.defaultAddress?.address2 || '',
        city: customer?.defaultAddress?.city || '',
        province: customer?.defaultAddress?.province || '',
        zip: customer?.defaultAddress?.zip || '',
        country: customer?.defaultAddress?.country || 'Ireland',
    });

    // Set page title for SEO
    usePageTitle('My Account', 'Manage your MAISON D\'OUD account, orders, and delivery details.');

    // Show loading state while auth context initialises
    if (loading) return (
        <div className="bg-background min-h-screen pt-32 flex items-center justify-center">
            <p className="text-muted-foreground animate-pulse">Loading your account...</p>
        </div>
    );

    // Redirect to login if not authenticated
    if (!customer) { navigate('/login'); return null; }

    // Log out and redirect home
    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    // Show success message and clear after 3 seconds
    const showSuccess = (msg) => {
        setSuccess(msg);
        setError('');
        setTimeout(() => setSuccess(''), 3000);
    };

    // Show error message and clear any existing success
    const showError = (msg) => {
        setError(msg);
        setSuccess('');
    };

    // Submit updated personal details to Shopify
    const handleUpdateDetails = async (e) => {
        e.preventDefault();
        const result = await updateDetails(detailsForm);
        if (result.error) showError(result.error);
        else showSuccess('Details updated successfully!');
    };

    // Submit updated delivery address to Shopify
    const handleUpdateAddress = async (e) => {
        e.preventDefault();
        const result = await updateAddress(addressForm);
        if (result.error) showError(result.error);
        else showSuccess('Address saved successfully!');
    };

    // Trigger password reset email via Shopify
    const handlePasswordReset = async () => {
        const result = await sendPasswordReset();
        if (result.error) showError(result.error);
        else showSuccess(`Password reset email sent to ${customer.email}`);
    };

    // Pull orders from Shopify customer object
    const orders = customer.orders?.edges || [];

    // Format date to Irish locale (e.g. 13 May 2026)
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IE', {
        year: 'numeric', month: 'long', day: 'numeric'
    });

    // Convert Shopify status codes to readable strings (e.g. PARTIALLY_FULFILLED -> Partially Fulfilled)
    const formatStatus = (status) => status?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

    // Tab definitions for the account navigation
    const tabs = [
        { id: 'orders', label: 'Orders', icon: Package },
        { id: 'details', label: 'My Details', icon: User },
        { id: 'address', label: 'Address', icon: MapPin },
        { id: 'security', label: 'Security', icon: Lock },
    ];

    // ── Order Detail View ─────────────────────────────────────────────────────
    // Shown when the user clicks on an order in the orders list
    if (selectedOrder) {
        const order = selectedOrder;
        const shippingAddress = order.shippingAddress;

        return (
            <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
                <div className="max-w-4xl mx-auto">

                    {/* Back to orders list */}
                    <button
                        onClick={() => setSelectedOrder(null)}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Orders
                    </button>

                    {/* Order number and fulfilment status badge */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl font-light text-foreground tracking-wide">Order #{order.orderNumber}</h1>
                            <p className="text-muted-foreground text-sm mt-1">{formatDate(order.processedAt)}</p>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full self-start sm:self-auto ${
                            order.fulfillmentStatus === 'FULFILLED'
                                ? 'bg-green-500/10 text-green-400'
                                : 'bg-[#CEAC8F]/10 text-[#CEAC8F]'
                        }`}>
                            {formatStatus(order.fulfillmentStatus)}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Left column — status, shipping address, payment breakdown */}
                        <div className="space-y-6">

                            {/* Order status timeline */}
                            <div className="bg-card rounded-2xl border border-border p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Truck className="w-5 h-5 text-[#CEAC8F]" />
                                    <h3 className="text-[#CEAC8F] text-xs tracking-widest uppercase">Status</h3>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-foreground text-sm">Order confirmed</p>
                                            <p className="text-muted-foreground text-xs">{formatDate(order.processedAt)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {order.fulfillmentStatus === 'FULFILLED'
                                            ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                            : <Clock className="w-4 h-4 text-[#CEAC8F] flex-shrink-0" />
                                        }
                                        <p className="text-foreground text-sm">
                                            {order.fulfillmentStatus === 'FULFILLED' ? 'Fulfilled' : 'Being prepared for shipment'}
                                        </p>
                                    </div>
                                </div>

                                {/* Tracking numbers from Shopify fulfilments */}
                                {order.successfulFulfillments && order.successfulFulfillments.length > 0 && (
                                    <div className="border-t border-border pt-4 mt-4 space-y-3">
                                        <p className="text-muted-foreground text-xs uppercase tracking-widest">Tracking</p>
                                        {order.successfulFulfillments.map((fulfillment, i) => (
                                            <div key={i}>
                                                {fulfillment.trackingCompany && (
                                                    <p className="text-muted-foreground text-sm mb-1">{fulfillment.trackingCompany}</p>
                                                )}
                                                {fulfillment.trackingInfo.map((tracking, j) => (
                                                    <div key={j}>
                                                        {tracking.url ? (
                                                            <a
                                                                href={tracking.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-[#CEAC8F] text-sm hover:underline"
                                                            >
                                                                {tracking.number}
                                                            </a>
                                                        ) : (
                                                            <p className="text-foreground text-sm">{tracking.number}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Shipping address */}
                            {shippingAddress && (
                                <div className="bg-card rounded-2xl border border-border p-6">
                                    <h3 className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-4">Shipping address</h3>
                                    <p className="text-foreground text-sm leading-relaxed">
                                        {shippingAddress.firstName} {shippingAddress.lastName}<br />
                                        {shippingAddress.address1}<br />
                                        {shippingAddress.address2 && <>{shippingAddress.address2}<br /></>}
                                        {shippingAddress.city}{shippingAddress.province ? `, ${shippingAddress.province}` : ''}<br />
                                        {shippingAddress.zip}<br />
                                        {shippingAddress.country}
                                    </p>
                                </div>
                            )}

                            {/* Payment breakdown — subtotal, shipping, tax, total */}
                            <div className="bg-card rounded-2xl border border-border p-6">
                                <h3 className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-4">Payment</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Subtotal</span>
                                        <span className="text-foreground">{getSymbol(order.subtotalPrice?.currencyCode)}{parseFloat(order.subtotalPrice?.amount || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Shipping</span>
                                        <span className="text-foreground">
                                            {parseFloat(order.totalShippingPrice?.amount || 0) === 0
                                                ? 'Free'
                                                : `${getSymbol(order.totalShippingPrice.currencyCode)}${parseFloat(order.totalShippingPrice.amount).toFixed(2)}`
                                            }
                                        </span>
                                    </div>
                                    {parseFloat(order.totalTax?.amount || 0) > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Tax</span>
                                            <span className="text-foreground">{getSymbol(order.totalTax.currencyCode)}{parseFloat(order.totalTax.amount).toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm border-t border-border pt-2 mt-2">
                                        <span className="text-foreground font-medium">Total</span>
                                        <span className="text-[#CEAC8F] font-medium">{getSymbol(order.currentTotalPrice.currencyCode)}{parseFloat(order.currentTotalPrice.amount).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right column — ordered items */}
                        <div>
                            <div className="bg-card rounded-2xl border border-border overflow-hidden">
                                <div className="p-6 border-b border-border">
                                    <h3 className="text-[#CEAC8F] text-xs tracking-widest uppercase">Items</h3>
                                </div>
                                <div className="divide-y divide-border">
                                    {order.lineItems.edges.map(({ node: item }, index) => (
                                        <div key={index} className="p-6 flex items-center gap-4">
                                            {item.variant?.image && (
                                                <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                                                    <img
                                                        src={item.variant.image.url}
                                                        alt={item.variant.image.altText || item.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-foreground text-sm font-medium truncate">{item.title}</p>
                                                {item.variant?.title && item.variant.title !== 'Default Title' && (
                                                    <p className="text-muted-foreground text-xs mt-0.5">{item.variant.title}</p>
                                                )}
                                                <p className="text-muted-foreground text-xs mt-1">Qty: {item.quantity}</p>
                                            </div>
                                            {item.variant?.price && (
                                                <p className="text-[#CEAC8F] text-sm flex-shrink-0">
                                                    {getSymbol(item.variant.price.currencyCode)}{parseFloat(item.variant.price.amount).toFixed(2)}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Main Account View ─────────────────────────────────────────────────────
    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
            <div className="max-w-4xl mx-auto">

                {/* Header — customer name and sign out button */}
                <div className="flex items-center justify-between mb-10">
                    <div>
                        <h1 className="text-4xl font-light text-foreground tracking-wide">
                            Welcome back, {customer.firstName} — MAISON D'OUD.
                        </h1>
                        <p className="text-muted-foreground text-sm mt-1">{customer.email}</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-muted-foreground hover:text-red-400 transition-colors text-sm"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>

                {/* Success and error feedback banners */}
                {success && <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">{success}</div>}
                {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}

                {/* Tab navigation */}
                <div className="flex gap-1 mb-8 bg-muted/30 rounded-xl p-1">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm transition-all ${
                                activeTab === id
                                    ? 'bg-background text-[#CEAC8F] shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="hidden sm:block">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Orders Tab — list of past orders, clickable for detail view */}
                {activeTab === 'orders' && (
                    <div>
                        {orders.length === 0 ? (
                            <div className="bg-card rounded-2xl p-12 border border-border text-center">
                                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">You haven't placed any orders yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map(({ node: order }) => (
                                    <div
                                        key={order.id}
                                        onClick={() => setSelectedOrder(order)}
                                        className="bg-card rounded-2xl border border-border overflow-hidden cursor-pointer hover:border-[#CEAC8F]/30 transition-colors"
                                    >
                                        <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div>
                                                <p className="text-foreground font-medium">Order #{order.orderNumber}</p>
                                                <p className="text-muted-foreground text-sm mt-1">{formatDate(order.processedAt)}</p>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className={`text-xs px-3 py-1 rounded-full ${order.fulfillmentStatus === 'FULFILLED' ? 'bg-green-500/10 text-green-400' : 'bg-[#CEAC8F]/10 text-[#CEAC8F]'}`}>
                                                    {formatStatus(order.fulfillmentStatus)}
                                                </span>
                                                <span className="text-[#CEAC8F] font-light">
                                                    {getSymbol(order.currentTotalPrice.currencyCode)}{parseFloat(order.currentTotalPrice.amount).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-6 space-y-4">
                                            {order.lineItems.edges.map(({ node: item }, index) => (
                                                <div key={index} className="flex items-center gap-4">
                                                    {item.variant?.image && (
                                                        <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                                                            <img src={item.variant.image.url} alt={item.variant.image.altText || item.title} className="w-full h-full object-cover" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="text-foreground text-sm font-medium">{item.title}</p>
                                                        <p className="text-muted-foreground text-xs mt-1">Qty: {item.quantity}</p>
                                                    </div>
                                                    {item.variant?.price && (
                                                        <p className="text-[#CEAC8F] text-sm">{getSymbol(item.variant.price.currencyCode)}{parseFloat(item.variant.price.amount).toFixed(2)}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Details Tab — update name and phone number */}
                {activeTab === 'details' && (
                    <div className="bg-card rounded-2xl p-8 border border-border">
                        <h2 className="text-xl font-light text-foreground mb-6">Personal Details</h2>
                        <form onSubmit={handleUpdateDetails} className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">First Name</label>
                                    <input type="text" value={detailsForm.firstName} onChange={e => setDetailsForm(p => ({ ...p, firstName: e.target.value }))} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">Last Name</label>
                                    <input type="text" value={detailsForm.lastName} onChange={e => setDetailsForm(p => ({ ...p, lastName: e.target.value }))} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-muted-foreground text-sm mb-2">Phone Number</label>
                                <input type="tel" value={detailsForm.phone} onChange={e => setDetailsForm(p => ({ ...p, phone: e.target.value }))} placeholder="+353 85 000 0000" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                            </div>
                            <button type="submit" disabled={authLoading} className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60">
                                {authLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Address Tab — update default delivery address */}
                {activeTab === 'address' && (
                    <div className="bg-card rounded-2xl p-8 border border-border">
                        <h2 className="text-xl font-light text-foreground mb-6">Delivery Address</h2>
                        <form onSubmit={handleUpdateAddress} className="space-y-5">
                            <div>
                                <label className="block text-muted-foreground text-sm mb-2">Address Line 1</label>
                                <input type="text" value={addressForm.address1} onChange={e => setAddressForm(p => ({ ...p, address1: e.target.value }))} placeholder="123 Main Street" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                            </div>
                            <div>
                                <label className="block text-muted-foreground text-sm mb-2">Address Line 2 (optional)</label>
                                <input type="text" value={addressForm.address2} onChange={e => setAddressForm(p => ({ ...p, address2: e.target.value }))} placeholder="Apartment, suite, etc." className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">City</label>
                                    <input type="text" value={addressForm.city} onChange={e => setAddressForm(p => ({ ...p, city: e.target.value }))} placeholder="Dublin" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">County</label>
                                    <input type="text" value={addressForm.province} onChange={e => setAddressForm(p => ({ ...p, province: e.target.value }))} placeholder="Dublin" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">Eircode / Postcode</label>
                                    <input type="text" value={addressForm.zip} onChange={e => setAddressForm(p => ({ ...p, zip: e.target.value }))} placeholder="D01 AB12" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-2">Country</label>
                                    <input type="text" value={addressForm.country} onChange={e => setAddressForm(p => ({ ...p, country: e.target.value }))} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors" />
                                </div>
                            </div>
                            <button type="submit" disabled={authLoading} className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60">
                                {authLoading ? 'Saving...' : 'Save Address'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Security Tab — send password reset email */}
                {activeTab === 'security' && (
                    <div className="bg-card rounded-2xl p-8 border border-border">
                        <h2 className="text-xl font-light text-foreground mb-2">Security</h2>
                        <p className="text-muted-foreground text-sm mb-8">To change your password, we'll send a reset link to your email address.</p>
                        <div className="bg-background rounded-lg p-4 border border-border mb-6">
                            <p className="text-muted-foreground text-sm">Email address</p>
                            <p className="text-foreground mt-1">{customer.email}</p>
                        </div>
                        <button onClick={handlePasswordReset} disabled={authLoading} className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60">
                            {authLoading ? 'Sending...' : 'Send Password Reset Email'}
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Account;